import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import { ApiService } from '../../../../core/services/api/api.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { environment } from '../../../../../environments/environment';

interface ReceiptRow {
  order_no: string;
  order_date: string;
  _sort_key: string;
  qty: number | null;
  pack_date: string;
  receive_date: string;
  shop_ref: string;
  branch_name: string;
  branch_color: string;
  branch_number: string;
}

@Component({
  selector: 'app-branch-receipt',
  templateUrl: './branch-receipt.component.html',
  styleUrl: './branch-receipt.component.css'
})
export class BranchReceiptComponent implements OnInit {
  faReceipt = faReceipt;

  channels: any[] = [];
  channelSelected: string[] = [];
  readonly ALL_CHANNEL_MARKER = '__all__';

  loading: boolean = false;
  rows: ReceiptRow[] = [];
  errorMessage: string = '';

  countDataSource = 0;
  pageSize = 20;
  pageIndex = 0;
  limit = 20;
  offset = 0;

  selectedDate: { type: string; value: string } = { type: 'date-range', value: '' };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private platformService: PlatformService,
    private utilService: UtilService,
  ) {
    this.channels = (platformService?.channels || []).filter(c => !!c.is_branch);
    const start = this.utilService.dayjs().subtract(90, 'day').format('YYYY-MM-DD');
    const end = this.utilService.dayjs().format('YYYY-MM-DD');
    this.selectedDate = { type: 'date-range', value: `${start},${end}` };
  }

  ngOnInit(): void {
    const channelQp = this.route.snapshot.queryParamMap.get('channel');
    if (channelQp) {
      this.channelSelected = channelQp.split(',')
        .filter(s => this.channels.some(c => c.shop_ref === s));
    }
    if (this.channelSelected.length === 0 && this.channels.length > 0) {
      this.channelSelected = [this.channels[0].shop_ref];
    }
    if (this.channelSelected.length > 0) {
      this.fetchReceipts();
    }
  }

  async fetchReceipts() {
    this.loading = true;
    this.errorMessage = '';
    this.rows = [];
    if (this.channelSelected.length === 0) {
      this.countDataSource = 0;
      this.loading = false;
      return;
    }
    const branches = this.channelSelected
      .map(ref => this.channels.find(c => c.shop_ref === ref))
      .filter(b => b && b.member_id);
    if (branches.length === 0) {
      this.errorMessage = 'ไม่พบ member_id ของสาขาที่เลือก';
      this.countDataSource = 0;
      this.loading = false;
      return;
    }
    try {
      const url = `${environment.endpoint}/receipt/get`;
      const { start, end } = this.resolveDateRange(this.selectedDate);
      const body: any = {
        member_id: branches.map(b => b!.member_id),
        start_date: start,
        end_date: end,
        limit: String(this.limit),
        offset: String(this.offset),
      };
      const res: any = await this.apiService.postCall(url, body);
      const memberMap = new Map<string, any>();
      for (const b of branches) {
        if (b?.member_id) memberMap.set(String(b.member_id), b);
      }
      this.rows = (res?.data || []).map((item: any) => {
        const rawDate = String(item?.order_date ?? '');
        const memberId = String(item?.member_id ?? '');
        const b = memberMap.get(memberId);
        return {
          order_no: item?.order_no ?? '-',
          order_date: this.formatDate(rawDate),
          _sort_key: rawDate,
          qty: null,
          pack_date: '',
          receive_date: '',
          shop_ref: b?.shop_ref || '',
          branch_name: b?.platform_name || '',
          branch_color: b?.color || '#32436a',
          branch_number: b?.branch_number || '',
        };
      });
      this.countDataSource = res?.count ?? this.rows.length;
      await this.fetchDetailsForRows();
    } catch (e: any) {
      this.errorMessage = e?.message || 'เกิดข้อผิดพลาดในการเรียก API';
      this.rows = [];
      this.countDataSource = 0;
    } finally {
      this.loading = false;
    }
  }

  private async fetchDetailsForRows() {
    const byBranch = new Map<string, string[]>();
    for (const row of this.rows) {
      if (!row.branch_number) continue;
      const orders = byBranch.get(row.branch_number) || [];
      orders.push(row.order_no);
      byBranch.set(row.branch_number, orders);
    }
    if (byBranch.size === 0) return;

    const url = `${environment.endpoint}/receipt/get/detail`;
    const branchKeys = Array.from(byBranch.keys());
    const results = await Promise.all(
      branchKeys.map(branch => this.apiService
        .postCall(url, { order_no: byBranch.get(branch), branch })
        .catch(() => null)
      )
    );

    const detailMap = new Map<string, any>();
    for (let i = 0; i < results.length; i++) {
      const branch = branchKeys[i];
      const data = (results[i] as any)?.data || [];
      for (const d of data) {
        detailMap.set(`${branch}:${d.order_no}`, d);
      }
    }

    this.rows = this.rows.map(r => {
      const d = detailMap.get(`${r.branch_number}:${r.order_no}`);
      return {
        ...r,
        qty: d?.qty ?? null,
        pack_date: this.formatDate(d?.pack_date),
        receive_date: this.formatDate(d?.receive_date),
      };
    });
  }

  private formatDate(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const d = typeof value === 'number'
      ? this.utilService.dayjs.unix(value > 1e12 ? Math.floor(value / 1000) : value)
      : this.utilService.dayjs(value);
    return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : String(value);
  }

  get displayedChannelSelection(): string[] {
    if (this.channels.length > 0 && this.channelSelected.length === this.channels.length) {
      return [this.ALL_CHANNEL_MARKER, ...this.channelSelected];
    }
    return this.channelSelected;
  }

  onChannelSelectionChange(values: any[]) {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.channels.length > 0
      && this.channelSelected.length === this.channels.length;
    const newHasMarker = list.includes(this.ALL_CHANNEL_MARKER);
    if (newHasMarker !== wasAllSelected) {
      this.channelSelected = newHasMarker
        ? this.channels.map(c => c.shop_ref)
        : [];
    } else {
      this.channelSelected = list.filter((v: unknown): v is string =>
        typeof v === 'string' && v !== this.ALL_CHANNEL_MARKER
      );
    }
    this.offset = 0;
    this.pageIndex = 0;
    this.fetchReceipts();
  }

  onDateOptionSelected(option: { type: string; value: string }) {
    this.selectedDate = option;
    this.offset = 0;
    this.pageIndex = 0;
    this.fetchReceipts();
  }

  private resolveDateRange(option: { type: string; value: string }): { start: string; end: string } {
    const dj = this.utilService.dayjs;
    const today = dj().format('YYYY-MM-DD');
    if (option.type === 'daypass') {
      if (option.value === '0') {
        return { start: today, end: today };
      }
      if (option.value === '1') {
        const yesterday = dj().subtract(1, 'day').format('YYYY-MM-DD');
        return { start: yesterday, end: yesterday };
      }
      if (option.value === 'month') {
        return { start: dj().startOf('month').format('YYYY-MM-DD'), end: today };
      }
      const days = parseInt(option.value, 10) || 0;
      return {
        start: dj().subtract(days, 'day').format('YYYY-MM-DD'),
        end: dj().subtract(1, 'day').format('YYYY-MM-DD'),
      };
    }
    if (option.type === 'month-year') {
      return {
        start: dj(option.value).startOf('month').format('YYYY-MM-DD'),
        end: dj(option.value).endOf('month').format('YYYY-MM-DD'),
      };
    }
    if (option.type === 'year') {
      return {
        start: dj(option.value).startOf('year').format('YYYY-MM-DD'),
        end: dj(option.value).endOf('year').format('YYYY-MM-DD'),
      };
    }
    if (option.type === 'date-range') {
      const [s, e] = (option.value || '').split(',');
      return {
        start: dj(s).format('YYYY-MM-DD'),
        end: dj(e).format('YYYY-MM-DD'),
      };
    }
    return { start: today, end: today };
  }

  openDetail(row: ReceiptRow) {
    this.router.navigate(['/branch/receipt/detail'], {
      queryParams: {
        order_no: row.order_no,
        channel: row.shop_ref,
        order_date: row.order_date,
      },
    });
  }

  callBackPageChange = (data: any): void => {
    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      this.fetchReceipts();
    }
    if (data.pageIndex !== undefined) this.pageIndex = data.pageIndex;
    if (data.limit !== undefined) this.pageSize = data.limit;
  }
}
